import { useMemo, useId } from "react";
import type { AccordionItemProps } from "./types";
import { AccordionItemContext, useAccordion } from "./AccordionContext";
import { cx } from "@/utils/cx";
import styles from "./Accordion.module.css";

/**
 * Accordion.Item component
 *
 * Wrapper for a single accordion panel (trigger + content pair).
 * Must be used within Accordion root.
 *
 * @example
 * ```tsx
 * <Accordion.Item value="section-1">
 *   <Accordion.Trigger>Title</Accordion.Trigger>
 *   <Accordion.Content>Content</Accordion.Content>
 * </Accordion.Item>
 * ```
 */
export function AccordionItem({
  value,
  disabled = false,
  className,
  "data-testid": testId,
  "data-edit-mode": editMode,
  children,
}: AccordionItemProps) {
  const accordion = useAccordion();
  const uniqueId = useId();

  // Determine if this item is expanded
  const isOpen = accordion.value.includes(value);

  // Determine if this item is disabled (item-level or root-level)
  const isDisabled = disabled || accordion.disabled;

  // Generate unique IDs for ARIA
  const triggerId = `accordion-trigger-${uniqueId}`;
  const contentId = `accordion-content-${uniqueId}`;

  // Memoize context value
  const contextValue = useMemo(
    () => ({
      value,
      triggerId,
      contentId,
      isOpen,
      isDisabled,
    }),
    [value, triggerId, contentId, isOpen, isDisabled],
  );

  return (
    <AccordionItemContext.Provider value={contextValue}>
      <div
        className={cx(styles.item, className)}
        data-accordion-item
        data-state={isOpen ? "open" : "closed"}
        data-disabled={isDisabled ? "" : undefined}
        data-testid={testId}
        data-edit-mode={editMode ? "true" : undefined}
      >
        {children}
      </div>
    </AccordionItemContext.Provider>
  );
}
